import { useEffect, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { FaEdit } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import countryPriceApi from "../../api/countryPriceApi";
import Pagination from "../../components/Pagination";

const CreditManagement = () => {
  const navigate = useNavigate();
  const [countryPricings, setCountryPricings] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortedColumn, setSortedColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [editMinPrice, setEditMinPrice] = useState("");
  const [editCurrencyCode, setEditCurrencyCode] = useState("");
  const [editCurrencySymbol, setEditCurrencySymbol] = useState("");

  useEffect(() => {
    fetchCountryPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortedColumn, sortOrder]);

  const fetchCountryPrices = async () => {
    try {
      const response = await countryPriceApi.getCompanyPricing(
        page,
        pageSize,
        sortedColumn,
        sortOrder,
      );
      setCountryPricings(response.data.content || []);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      console.error("Error fetching country prices:", error);
      setCountryPricings([]);
      setTotalPages(0);
    }
  };

  const handleEditClick = (pricing) => {
    // Navigate to AddCredits page with the selected pricing for editing
    navigate('add-credits', { state: pricing });
  };

  const handleEditSave = async () => {
    if (!editingPricing) return;
    const payload = {
      minPrice: parseFloat(editMinPrice),
      currencyCode: editCurrencyCode,
      currencySymbol: editCurrencySymbol,
    };
    try {
      await countryPriceApi.updateCountryPrice(editingPricing.id, payload);
      setShowEditModal(false);
      fetchCountryPrices();
    } catch (error) {
      console.error("Error updating pricing:", error);
    }
  };

  const handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value);
    setPageSize(size);
    setPage(0);
  };

  const handlePageClick = (data) => {
    const selectedPage = Math.max(0, Math.min(data.selected, totalPages - 1));
    setPage(selectedPage);
    localStorage.setItem("userCreditListAdmin", selectedPage);
  };

  const handleAddCredits = () => {
    navigate("/master-admin-dashboard/credit-management/add-credits");
  };

  return (
    <div className="scrollable-container" style={{ height: "100%" }}>
      <h1>
        <strong>Credit Management</strong>
      </h1>

      <div className="d-flex align-items-center justify-content-between mb-3">
        <p>This is the Credit Management page for setting fryme prices by country.</p>
        <Button variant="primary" onClick={handleAddCredits}>
          Add Credits
        </Button>
      </div>
      <Table striped bordered hover className="mt-3">
        <thead>
          <tr>
            <th>#</th>
            <th>Country</th>
            <th>Base Fryme Price</th>
            <th>Visible Roles</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {countryPricings.map((pricing, index) => (
            <tr key={pricing.countryCode}>
              <td>{index + 1}</td>
              <td>{pricing.countryCode}</td>
              <td>{pricing.minPrice} {pricing.currencySymbol} - ({pricing.currencyCode})</td>
              <td>
                {pricing.visibleToRoles && pricing.visibleToRoles.length > 0
                  ? pricing.visibleToRoles.join(", ")
                  : "-"}
              </td>
              <td>
                <Button variant="warning" size="sm" onClick={() => handleEditClick(pricing)}>
                  <FaEdit />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div style={{ marginTop: "auto" }}>
        {countryPricings.length > 0 && totalPages > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            handlePageSizeChange={handlePageSizeChange}
            handlePageClick={handlePageClick}
          />
        )}
      </div>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Country Pricing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formMinPrice">
              <Form.Label>Minimum Price</Form.Label>
              <Form.Control
                type="number"
                value={editMinPrice}
                onChange={(e) => setEditMinPrice(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formCurrencyCode">
              <Form.Label>Currency Code</Form.Label>
              <Form.Control
                type="text"
                value={editCurrencyCode}
                onChange={(e) => setEditCurrencyCode(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formCurrencySymbol">
              <Form.Label>Currency Symbol</Form.Label>
              <Form.Control
                type="text"
                value={editCurrencySymbol}
                onChange={(e) => setEditCurrencySymbol(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSave}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CreditManagement;
